import AlertTriangle from '@assets/icons/alert-triangle.svg';
import AttachmentsIcon from '@assets/icons/attachment.svg';
import i18n from '@/utils/i18n';
import { type FC } from 'react';
import { type OrderInboundOption } from '@/screens/order-inbounds/types';
import { StyleSheet, View } from 'react-native';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useSupplierDynamicConfigFlag } from '@/hooks/use-dynamic-config-value';
import { useTheme } from '@/contexts/theme-context';

interface Props {
  option: OrderInboundOption;
}

export const OrderInboundsOrderListItem: FC<Props> = ({ option }) => {
  const theme = useTheme();
  const isEmailThreadsEnabled = useSupplierDynamicConfigFlag(
    'smart_order_config',
    'email_threads_enabled',
  );

  const numItemsText =
    option.metadata &&
    typeof option.metadata.numItems === 'number' &&
    option.metadata.numItems > 0
      ? option.metadata.numItems > 1
        ? i18n.t('order_inbound.items_plural', {
            count: option.metadata.numItems,
          })
        : i18n.t('order_inbound.item_singular', {
            count: option.metadata.numItems,
          })
      : '';

  return (
    <View style={styles.itemContainer}>
      <View style={styles.itemHeader}>
        <View style={styles.itemTitle}>
          <Typography numberOfLines={1} variant="paragraph-md-600">
            {option.title}
          </Typography>

          {isEmailThreadsEnabled &&
          Boolean(option.metadata?.threadCount) &&
          option.metadata?.threadCount! > 1 ? (
            <Typography
              color={theme.sk.color.text.tertiary}
              numberOfLines={1}
              variant="paragraph-md"
            >
              {option.metadata?.threadCount}
            </Typography>
          ) : null}
        </View>

        <Typography
          color={theme.sk.color.text.secondary}
          numberOfLines={1}
          variant="paragraph-md"
        >
          {option.metadata?.time}
        </Typography>
      </View>

      <Typography
        color={theme.sk.color.text.primary}
        numberOfLines={1}
        variant="paragraph-md"
      >
        {option.subtitle}
      </Typography>

      <View style={styles.itemFooter}>
        {Boolean(option.metadata?.unknownContactInfo) &&
        !option.metadata?.isProcessing ? (
          <AlertTriangle
            height={16}
            stroke={theme.sk.color.icon.warning.secondary}
            width={16}
          />
        ) : null}

        <Typography
          color={theme.sk.color.text.secondary}
          variant="paragraph-sm"
        >
          {option.metadata?.contactName}{' '}
          {numItemsText !== '' ? `• ${numItemsText}` : ''}
        </Typography>

        {Boolean(option.metadata) && option.metadata!.hasAttachments ? (
          <AttachmentsIcon
            height={16}
            stroke={theme.sk.color.icon.tertiary}
            width={16}
          />
        ) : null}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  itemContainer: {
    flex: 1,
    gap: 2,
  },
  itemFooter: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 8,
  },
  itemHeader: {
    alignItems: 'flex-start',
    flex: 1,
    flexDirection: 'row',
    gap: 8,
    justifyContent: 'space-between',
  },
  itemTitle: {
    flexDirection: 'row',
    flexShrink: 1,
    gap: 8,
  },
});
